import React, { useState, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, ChevronRight, Heart } from 'lucide-react';
import { MOOD_LEVELS, ENERGY_LEVELS } from '../hooks/useMoodTracking';
import { Z_CLASS } from '../../../constants/zIndex';
import { haptic } from '../../../shared/utils/haptics';
import { cn } from '../../../shared/utils/cn';

/**
 * MoodCheckInModal - Morning mood check-in modal
 * Multi-step flow: mood -> energy -> optional note
 */
export const MoodCheckInModal = memo(function MoodCheckInModal({ onSave, onSkip }) {
  const [step, setStep] = useState(1);
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedEnergy, setSelectedEnergy] = useState(null);
  const [note, setNote] = useState('');

  const handleMoodSelect = useCallback((mood) => {
    haptic.tap();
    setSelectedMood(mood);
    setStep(2);
  }, []);

  const handleEnergySelect = useCallback((energy) => {
    haptic.tap();
    setSelectedEnergy(energy);
    if (selectedMood <= 2) {
      setStep(3);
    } else {
      handleSave(energy);
    }
  }, [selectedMood]);

  const handleSave = useCallback((energy = selectedEnergy) => {
    haptic.success();
    onSave(selectedMood, energy, note);
  }, [selectedMood, selectedEnergy, note, onSave]);

  const handleSkip = useCallback(() => {
    haptic.tap();
    onSkip();
  }, [onSkip]);

  return (
    <motion.div
      className={cn(
        'fixed inset-0 flex items-center justify-center p-4 overflow-hidden',
        'glass-overlay',
        Z_CLASS.MOOD_CHECK_IN
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-sm glass-elevated p-6"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <motion.div
            className="w-12 h-12 mx-auto glass-card flex items-center justify-center mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Sun className="w-5 h-5 text-zinc-500" />
          </motion.div>
          <motion.h2
            className="text-base font-medium text-zinc-100"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Bonjour
          </motion.h2>
          <motion.p
            className="text-zinc-500 text-sm mt-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Comment te sens-tu ce matin ?
          </motion.p>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Mood selection */}
          {step === 1 && (
            <motion.div
              key="mood"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="flex justify-center gap-2 mb-4">
                {Object.entries(MOOD_LEVELS).map(([level, data], index) => (
                  <motion.button
                    key={level}
                    onClick={() => handleMoodSelect(parseInt(level))}
                    className="flex flex-col items-center gap-1 p-3 glass-interactive"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-3xl">{data.emoji}</span>
                    <span className="text-[10px] text-zinc-500">{data.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Energy selection */}
          {step === 2 && (
            <motion.div
              key="energy"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="text-center mb-4">
                <span className="text-4xl">{MOOD_LEVELS[selectedMood]?.emoji}</span>
                <p className="text-zinc-400 text-sm mt-2">Quel est ton niveau d'énergie ?</p>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {Object.entries(ENERGY_LEVELS).map(([level, data], index) => (
                  <motion.button
                    key={level}
                    onClick={() => handleEnergySelect(parseInt(level))}
                    className="flex flex-col items-center gap-2 p-3 glass-interactive"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4].map(bar => (
                        <div
                          key={bar}
                          className={cn(
                            'w-1 rounded-full transition-colors',
                            bar <= data.bars ? 'bg-zinc-400' : 'bg-zinc-700'
                          )}
                          style={{ height: bar <= data.bars ? `${8 + bar * 2}px` : '6px' }}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] text-zinc-500">{data.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Optional note (for low mood) */}
          {step === 3 && (
            <motion.div
              key="note"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="text-center mb-4">
                <Heart className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                <p className="text-zinc-200 font-medium text-sm">
                  Les jours difficiles arrivent, et c'est OK.
                </p>
                <p className="text-zinc-500 text-xs mt-1">
                  Veux-tu noter quelque chose ? (optionnel)
                </p>
              </div>

              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Comment tu te sens, ce qui te préoccupe..."
                className="w-full h-24 p-3 glass-card text-zinc-200 text-sm placeholder-zinc-600 resize-none focus:outline-none focus:border-zinc-600 transition-colors"
              />

              <div className="flex gap-2 mt-4">
                <motion.button
                  onClick={() => handleSave()}
                  className="flex-1 py-2.5 bg-zinc-800 text-zinc-400 rounded-lg font-medium text-sm hover:bg-zinc-700 transition-colors"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  Passer
                </motion.button>
                <motion.button
                  onClick={() => handleSave()}
                  className="flex-1 py-2.5 bg-zinc-700 text-white rounded-lg font-medium text-sm hover:bg-zinc-600 flex items-center justify-center gap-1 transition-colors"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  Continuer
                  <ChevronRight className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Adaptive message */}
        <AnimatePresence>
          {step >= 2 && selectedMood && (
            <motion.div
              className="mt-4 p-3 glass-subtle text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <p className="text-zinc-500 text-xs">
                {selectedMood <= 2 ? (
                  "Aujourd'hui, concentre-toi sur l'essentiel."
                ) : selectedMood >= 4 ? (
                  "Bonne énergie aujourd'hui !"
                ) : (
                  "Un pas à la fois."
                )}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip button */}
        {step === 1 && (
          <motion.button
            onClick={handleSkip}
            className="w-full mt-4 py-2 text-zinc-500 text-sm hover:text-zinc-400 transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Passer pour aujourd'hui
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
});

export default MoodCheckInModal;
